import React, { useState, useEffect } from 'react';
import Loading from './Loading';

// Static libraries array - must be defined outside component to prevent reload warnings
const GOOGLE_MAPS_LIBRARIES = process.env.REACT_APP_GOOGLE_LIBRARIES
    ? process.env.REACT_APP_GOOGLE_LIBRARIES.split(',')
    : ['places'];

/**
 * GoogleMapsLoader - Component that lazy loads Google Maps API and provides isLoaded state
 *
 * This reduces initial bundle size by ~100-150 kB by dynamically importing
 * @react-google-maps/api only when this component is rendered.
 *
 * Usage:
 * <GoogleMapsLoader onLoadedChange={setIsLoaded}>
 *   {(isLoaded) => <YourComponent isLoaded={isLoaded} />}
 * </GoogleMapsLoader>
 */
const GoogleMapsLoader = ({ children, onLoadedChange }) => {
    const [mapsModule, setMapsModule] = useState(null);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadGoogleMaps = async () => {
            try {
                // Dynamically import Google Maps API only when needed
                const googleMapsModule = await import('@react-google-maps/api');
                if (!isMounted) return;
                setMapsModule(googleMapsModule);
            } catch (error) {
                if (isMounted) {
                    setLoadError(error);
                    console.error('Failed to load Google Maps API:', error);
                }
            }
        };

        loadGoogleMaps();

        return () => {
            isMounted = false;
        };
    }, []);

    // Once module is loaded, use the hook in a child component
    if (loadError) {
        return <div>Error loading Google Maps: {loadError.message}</div>;
    }

    if (!mapsModule) {
        return <Loading />;
    }

    // Render inner component that can use the hook
    return <GoogleMapsLoaderInner mapsModule={mapsModule} children={children} setIsLoaded={onLoadedChange} />;
};

/**
 * Inner component that uses useJsApiLoader hook
 * This component is only rendered after mapsModule is loaded
 */
const GoogleMapsLoaderInner = ({ mapsModule, children, setIsLoaded }) => {
    const { useJsApiLoader } = mapsModule;

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: JSON.parse(process.env.REACT_APP_GOOGLE).key,
        libraries: GOOGLE_MAPS_LIBRARIES
    });

    useEffect(() => {
        if (setIsLoaded) {
            setIsLoaded(isLoaded);
        }
    }, [isLoaded, setIsLoaded]);

    if (loadError) {
        return <div>Error loading Google Maps: {loadError.message}</div>;
    }

    if (!isLoaded) {
        return <Loading />;
    }

    // Render children with isLoaded prop
    return typeof children === 'function' 
        ? children(isLoaded)
        : React.cloneElement(children, { isLoaded });
};

export default GoogleMapsLoader;

